const SUPPORTED_LANGS = ["ar", "en"];

const ensureLeadingSlash = (value = "") => {
  const path = String(value || "").trim();
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
};

export const normalizeLang = (lang, fallback = "ar") => {
  const next = String(lang || "").toLowerCase();
  if (SUPPORTED_LANGS.includes(next)) return next;
  return SUPPORTED_LANGS.includes(fallback) ? fallback : "ar";
};

export const getStoredLang = () => {
  if (typeof window === "undefined") return "ar";
  const saved = localStorage.getItem("tns:dashboard_language") || "ar";
  return normalizeLang(saved);
};

export const getPreferredLang = () => getStoredLang();

export const getLangFromPathname = (pathname = "") => {
  const first = ensureLeadingSlash(pathname).split("/").filter(Boolean)[0] || "";
  return SUPPORTED_LANGS.includes(first) ? first : null;
};

export const stripLangPrefix = (pathname = "") => {
  const parts = ensureLeadingSlash(pathname).split("/").filter(Boolean);
  if (parts.length && SUPPORTED_LANGS.includes(parts[0])) parts.shift();
  return parts.length ? `/${parts.join("/")}` : "/";
};

export const buildLocalizedPath = (lang, pathname = "/") => {
  const nextLang = normalizeLang(lang);
  const stripped = stripLangPrefix(pathname);
  if (stripped === "/") return `/${nextLang}`;
  return `/${nextLang}${stripped}`;
};

export const buildLocaleRootPath = (lang) => `/${normalizeLang(lang)}`;

export const swapLangInPath = (pathname, nextLang) => buildLocalizedPath(nextLang, pathname);

export const buildDashboardPath = (lang, section = "profile") =>
  buildLocalizedPath(lang, `/dashboard/${section || "profile"}`);

export const buildServerPath = (lang, guildId, subPath = "server-overview") => {
  const gid = String(guildId || "").trim();
  const suffix = String(subPath || "server-overview").replace(/^\/+/, "");
  if (!gid) return buildLocalizedPath(lang, `/dashboard/profile`);
  return buildLocalizedPath(lang, `/server/${encodeURIComponent(gid)}/${suffix}`);
};

export const parseAppPath = (pathname = "") => {
  const normalized = ensureLeadingSlash(pathname);
  const parts = normalized.split("/").filter(Boolean);
  const lang = parts[0] && SUPPORTED_LANGS.includes(parts[0]) ? parts[0] : null;
  if (!lang) return { lang: null, scope: "public", guildId: "", subPath: "", section: "" };

  if (parts[1] === "dashboard") {
    return {
      lang,
      scope: "dashboard",
      guildId: "",
      subPath: "",
      section: parts[2] || "profile",
    };
  }

  if (parts[1] === "server" && parts[2]) {
    return {
      lang,
      scope: "server",
      guildId: decodeURIComponent(parts[2]),
      subPath: parts.slice(3).join("/") || "server-overview",
      section: "",
    };
  }

  return { lang, scope: "other", guildId: "", subPath: "", section: "" };
};

export const mapLegacyPathToCanonical = (path, lang, guildId = "") => {
  const value = String(path || "").trim();
  if (!value) return buildDashboardPath(lang, "profile");
  if (value.startsWith("/dashboard/")) {
    const subPath = value.replace(/^\/dashboard\//, "");
    return buildServerPath(lang, guildId, subPath);
  }
  if (value === "/profile" || value.startsWith("/profile/")) {
    const section = value.replace(/^\/profile\/?/, "") || "profile";
    return buildDashboardPath(lang, section);
  }
  return buildLocalizedPath(lang, value);
};

